import React, { useEffect, useState } from "react";
import BrandManagerShell from "./components/BrandManagerShell";
import apiService from "./services/api";
import "./brandManagerPages.css";

function unwrapData(payload) {
  return payload?.data ?? payload;
}

const METRICS = [
  "Messages Played",
  "Songs Played",
  "Active Devices",
  "Active Locations",
];

/**
 * Brand Performance — production shell.
 * Brand filter options come from canonical portfolio memberships.
 * Playback / active-device metrics are unavailable until the analytics plane exists.
 * Do not substitute Activity History, Song History, or Music upload history.
 */
export default function BrandManagerPerformance() {
  const [brands, setBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState("all");
  const [dateRange, setDateRange] = useState("30d");
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mineRaw = await apiService.listMyBrands();
        let catalog = [];
        try {
          const catalogRaw = await apiService.listBrandCatalog();
          catalog = unwrapData(catalogRaw)?.brands || [];
        } catch {
          catalog = [];
        }
        const mine = unwrapData(mineRaw);
        const nameById = new Map(
          catalog
            .filter((b) => b && (b.id || b.brandId))
            .map((b) => [b.id || b.brandId, b.name || b.brandName || null]),
        );
        const memberships = Array.isArray(mine?.brandMemberships)
          ? mine.brandMemberships
          : [];
        const options = memberships
          .filter(
            (m) =>
              m &&
              m.brandId &&
              (m.status == null || m.status === "active"),
          )
          .map((m) => ({
            id: m.brandId,
            name:
              m.brandName ||
              m.name ||
              nameById.get(m.brandId) ||
              m.brandId,
          }));
        if (!cancelled) {
          setBrands(options);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setBrands([]);
          setLoadError(err?.message || "Failed to load brand filter options");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BrandManagerShell>
      <div className="brand-manager-header">
        <h1>Brand Performance</h1>
        <p className="subtitle">
          Portfolio performance for Brands on your Brand Manager
        </p>
      </div>

      <div
        data-testid="brand-performance-page"
        data-performance-state="unavailable"
      >
        <div className="bm-toolbar">
          <select
            className="bm-select"
            aria-label="Date range"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            data-testid="brand-performance-date-range"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <select
            className="bm-select"
            aria-label="Brand filter"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            data-testid="brand-performance-brand-filter"
          >
            <option value="all">All Brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {loadError ? (
          <p className="bm-inline-status bm-inline-status--error">{loadError}</p>
        ) : null}

        <div className="bm-metric-grid" data-testid="brand-performance-metrics">
          {METRICS.map((label) => (
            <div className="bm-metric-card" key={label}>
              <div className="label">{label}</div>
              <div className="value">—</div>
            </div>
          ))}
        </div>

        <div className="bm-chart-grid">
          <div className="bm-chart-card">
            <h3>Messages Played</h3>
            <div className="bm-chart-placeholder">Chart unavailable</div>
          </div>
          <div className="bm-chart-card">
            <h3>Songs Played</h3>
            <div className="bm-chart-placeholder">Chart unavailable</div>
          </div>
        </div>

        <div
          className="bm-unavailable"
          style={{ marginTop: 16 }}
          data-testid="brand-performance-unavailable"
        >
          <h2>Analytics data not available yet</h2>
          <p>
            Live play and device metrics require the canonical telemetry /
            analytics plane. This page does not invent totals, trends, or
            chart series.
          </p>
          <p className="bm-unavailable-note">
            Brand filter options above use your Brand Manager portfolio
            memberships when available. Date range is UI-only until a
            performance read API exists.
          </p>
        </div>
      </div>
    </BrandManagerShell>
  );
}
